/**
 * URL Utilities
 *
 * Provides utility functions for URL handling, including:
 * - Normalizing URLs to HTTPS
 * - Validating URLs
 * - Handling image URLs
 */

/**
 * Normalize URL to HTTPS
 * Converts http:// to https:// to avoid mixed content warnings
 *
 * @param {string} url - The URL to normalize
 * @returns {string} The normalized URL with https://
 */
export const normalizeToHttps = (url) => {
  if (!url) return url;

  // If it's a relative URL, return as-is
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return url;
  }

  // Convert http:// to https://
  return url.replace(/^http:\/\//, "https://");
};

/**
 * Normalize image URL to HTTPS
 * Wrapper around normalizeToHttps for semantic clarity
 *
 * @param {string} imageUrl - The image URL to normalize
 * @returns {string} The normalized image URL
 */
export const normalizeImageUrl = (imageUrl) => {
  return normalizeToHttps(imageUrl);
};

/**
 * Normalize multiple URLs in an object
 * Recursively normalizes all URL-like string properties
 *
 * @param {Object} obj - Object containing URLs
 * @param {Array<string>} urlFields - Field names that contain URLs
 * @returns {Object} Object with normalized URLs
 */
export const normalizeUrlsInObject = (obj, urlFields = []) => {
  if (!obj || typeof obj !== "object") return obj;

  const normalized = { ...obj };

  urlFields.forEach((field) => {
    if (normalized[field] && typeof normalized[field] === "string") {
      normalized[field] = normalizeToHttps(normalized[field]);
    }
  });

  return normalized;
};

/**
 * Normalize URLs in an array of objects
 *
 * @param {Array<Object>} items - Array of objects containing URLs
 * @param {Array<string>} urlFields - Field names that contain URLs
 * @returns {Array<Object>} Array with normalized URLs
 */
export const normalizeUrlsInArray = (items, urlFields = []) => {
  if (!Array.isArray(items)) return items;

  return items.map((item) => normalizeUrlsInObject(item, urlFields));
};
